import { Box } from '@ltvco/refresh-lib/theme';
import React, { useState } from 'react';

import { FloatingLabel } from '../components';

export interface WithFloatingLabelProps {
  label?: string;
  value?: string;
  error?: any;
  hideLabel?: boolean;
}

/**
 * HOC that adds a floating label to an input component
 * The label floats above the input when the input is focused or has a value
 *
 * @param Component The component to enhance with a floating label
 * @returns A new component with a floating label
 */
export const withFloatingLabel = <P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P & WithFloatingLabelProps> => {
  return ({
    label = '',
    value = '',
    error,
    hideLabel = false,
    ...props
  }: WithFloatingLabelProps & P) => {
    const [isFocused, setIsFocused] = useState(false);

    // Forward these event handlers to the wrapped component
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      if ((props as any).onFocus) {
        (props as any).onFocus(e);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (!e.target.value) {
        setIsFocused(false);
      }
      if ((props as any).onBlur) {
        (props as any).onBlur(e);
      }
    };

    // Label should be visible when input is focused or has a value
    const shouldShowLabel = !hideLabel && (isFocused || !!value);
    const hasError = !!error;

    const enhancedProps = {
      ...(props as P),
      onFocus: handleFocus,
      onBlur: handleBlur,
    };

    const labelClassName = [
      shouldShowLabel ? 'focused' : '',
      hasError ? 'error' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Box sx={{ position: 'relative', width: '100%' }}>
        {label && (
          <FloatingLabel shrink className={labelClassName}>
            {label}
          </FloatingLabel>
        )}
        <Component {...enhancedProps} />
      </Box>
    );
  };
};
